import { gql } from "@apollo/client";

import { GetMeasurementStation_station_primary_datasource_alerts } from "./__generated__/GetMeasurementStation";
import { GetMeasurementStations_station } from "./__generated__/GetMeasurementStations";
import { alertFragment } from "../pages/__generated__/alertFragment";
import { useQuery } from "@apollo/client";
import { GetTriggeredAlertsQuery } from "utils/__generated__/GetTriggeredAlertsQuery";
import { GetTriggeredStationAlertsQuery } from "utils/__generated__/GetTriggeredStationAlertsQuery";

export const ALERT_LEVEL_NONE = 0;
export const ALERT_LEVEL_YELLOW = 1;
export const ALERT_LEVEL_RED = 2;

export const STATION_ORIGIN_OMGOS = "omgos";
export const STATION_ORIGIN_FMI = "fmi";
export const STATION_ORIGIN_SNOWER = "snower";

export const STATION_VISIBILITY_PRIVATE = "private";
export const STATION_VISIBILITY_PUBLIC = "public";

export const useAlertStatus = ({
  alerts,
  startDate,
  endDate,
}: {
  alerts:
    | alertFragment[]
    | GetMeasurementStation_station_primary_datasource_alerts[];
  startDate?: Date;
  endDate?: Date;
}) => {
  const currentTimestamp = new Date();
  const yesterdayTimestamp = new Date(
    currentTimestamp.getTime() - 86400 * 1000,
  );
  const filterStartDate = startDate ?? yesterdayTimestamp;
  const filterEndDate = endDate ?? currentTimestamp;
  const activeAlertCount = alerts.filter((alert) => alert.active).length;

  const { data: triggeredAlertsData } = useQuery<GetTriggeredAlertsQuery>(
    GET_TRIGGERED_ALERTS,
    {
      skip: !alerts.length,
      variables: {
        sourceUUID: alerts[0]?.source_uuid,
        minTimestamp: filterStartDate,
        maxTimestamp: filterEndDate,
      },
    },
  );

  return {
    activeAlertCount,
    isAlertTriggered: !!triggeredAlertsData?.alert_history?.length,
  };
};

export const useStationAlertLevels = ({
  stationIds,
  startDate,
  endDate,
}: {
  stationIds: number[];
  startDate?: Date;
  endDate?: Date;
}) => {
  const currentTimestamp = new Date();
  const yesterdayTimestamp = new Date(
    currentTimestamp.getTime() - 86400 * 1000,
  );
  const filterStartDate = startDate ?? yesterdayTimestamp;
  const filterEndDate = endDate ?? currentTimestamp;

  const { data: triggeredStationAlertsData } =
    useQuery<GetTriggeredStationAlertsQuery>(GET_TRIGGERED_STATION_ALERTS, {
      skip: !stationIds.length,
      variables: {
        stationIds: stationIds,
        minTimestamp: filterStartDate,
        maxTimestamp: filterEndDate,
      },
    });

  const alertLevelsPerStationId: Record<string, number> = {};
  triggeredStationAlertsData?.alert_history.forEach((alert) => {
    if (!alert?.alert?.station_id) {
      return;
    }
    alertLevelsPerStationId[alert.alert.station_id] = ALERT_LEVEL_RED;
  });
  return alertLevelsPerStationId;
};

/**
 * Shared station queries
 */

export const GET_MANAGED_STATION_LIST = gql`
  query GetManagedStationList($userId: Int!) {
    station(
      where: {
        organization: {
          organization_users: {
            role: { _eq: "admin" }
            user_id: { _eq: $userId }
          }
        }
      }
    ) {
      id
      identifier
      name
      organization_id
      lat
      lon
    }
  }
`;

export const GET_STATION_LIST = gql`
  query GetStationList {
    station {
      id
      identifier
      name
      organization_id
      lat
      lon
    }
  }
`;

export const GET_MEASUREMENT_STATION_QUERY = gql`
  query GetMeasurementStationQuery($identifier: String, $visible: [Boolean!]!) {
    station(where: { identifier: { _eq: $identifier } }) {
      id
      identifier
      name
      lat
      lon
      reference_lat
      reference_lon
      organization_id
      map_layer
      fmi_station_networks {
        fmi_network {
          name
          weather_enabled
          code
        }
      }
      distance_alert_threshold
      last_distance_alert_timestamp
      station_widgets {
        station_id
        base_source_uuid
        axis_scale_manual
        axis_scale_min
        axis_scale_max
        station_widget_sources {
          source_uuid
        }
      }
      datasources(where: { visible: { _in: $visible } }) {
        type
        source_id
        source_uuid
        unit {
          id
          name
          symbol
        }
        name
        visible
        order
        widget_size
        datasource_annotation {
          id
          timestamp
          value
          text
          color
        }
      }
    }
  }
`;

export const GET_TRIGGERED_ALERTS = gql`
  query GetTriggeredAlertsQuery(
    $sourceUUID: uuid!
    $minTimestamp: timestamptz!
    $maxTimestamp: timestamptz!
  ) {
    alert_history(
      where: {
        alert: { source_uuid: { _eq: $sourceUUID } }
        timestamp: { _gte: $minTimestamp, _lte: $maxTimestamp }
      }
    ) {
      id
      timestamp
      exceeding_value
    }
  }
`;

export const GET_TRIGGERED_STATION_ALERTS = gql`
  query GetTriggeredStationAlertsQuery(
    $stationIds: [Int!]!
    $minTimestamp: timestamptz!
    $maxTimestamp: timestamptz!
  ) {
    alert_history(
      where: {
        alert: { station_id: { _in: $stationIds } }
        timestamp: { _gte: $minTimestamp, _lte: $maxTimestamp }
      }
    ) {
      id
      timestamp
      exceeding_value
      alert {
        station_id
      }
    }
  }
`;

export const GET_MEASUREMENT_STATION_META = gql`
  query GetMeasurementStationMetaQuery($identifier: String) {
    station(where: { identifier: { _eq: $identifier } }) {
      id
      identifier
      name
      lat
      lon
      reference_lat
      reference_lon
      organization_id
      fmi_station_networks {
        fmi_network {
          name
          weather_enabled
          code
        }
      }
      distance_alert_threshold
      last_distance_alert_timestamp
      primary_datasource {
        name
        type
        source_id
        source_uuid
        unit {
          symbol
        }
        alerts {
          id
          source_uuid
          lower_limit
          upper_limit
          channel_id
          formula_id
          active
          level
          alert_frequency_minutes
          alert_timeframe_minutes
        }
      }
    }
  }
`;

export const GET_MEASUREMENT_STATION_META_BY_ID = gql`
  query GetMeasurementStationMetaByIdQuery($id: Int!) {
    station(where: { id: { _eq: $id } }) {
      id
      identifier
      name
      lat
      lon
      organization_id
    }
  }
`;

export const GET_MEASUREMENT_STATION_MEAN = gql`
  query GetMeasurementStationMeanQuery($identifier: String) {
    station(where: { identifier: { _eq: $identifier } }) {
      id
      mean_24h {
        value
        latest_value
        timestamp
      }
    }
  }
`;

export const GET_MEASUREMENT_STATION_SUBSCRIPTION = gql`
  subscription GetMeasurementStation(
    $identifier: String
    $visible: [Boolean!]!
  ) {
    station(where: { identifier: { _eq: $identifier } }) {
      id
      identifier
      name
      lat
      lon
      status
      battery_status
      reference_lat
      reference_lon
      organization_id
      map_layer
      origin
      visibility
      distance_alert_threshold
      last_distance_alert_timestamp
      fmi_station_networks {
        fmi_network {
          name
          weather_enabled
          code
        }
      }
      primary_datasource {
        name
        type
        source_id
        source_uuid
        unit {
          symbol
        }
        alerts {
          id
          source_uuid
          lower_limit
          upper_limit
          channel_id
          formula_id
          active
          level
          alert_frequency_minutes
          alert_timeframe_minutes
        }
      }
      station_widgets {
        station_id
        base_source_uuid
        axis_scale_manual
        axis_scale_min
        axis_scale_max
        station_widget_sources {
          source_uuid
        }
      }
      datasources(where: { visible: { _in: $visible } }) {
        type
        source_id
        source_uuid
        unit {
          id
          name
          symbol
        }
        name
        visible
        order
        widget_size
        datasource_annotation {
          id
          timestamp
          value
          text
          color
        }
      }
    }
  }
`;

export const GET_MEASUREMENT_STATIONS = gql`
  query GetMeasurementStations {
    station(order_by: { identifier: asc }) {
      id
      identifier
      name
      lon
      lat
      reference_lat
      reference_lon
      status
      origin
      visibility
      distance_alert_threshold
      last_distance_alert_timestamp
      organization {
        name
      }
      fmi_station_networks {
        fmi_network {
          name
          weather_enabled
          code
        }
      }
      last_data_received {
        station_id
        received_at
      }
      primary_datasource {
        name
        type
        source_id
        source_uuid
        unit {
          symbol
        }
        alerts {
          id
          source_uuid
          lower_limit
          upper_limit
          channel_id
          formula_id
          active
          level
          alert_frequency_minutes
          alert_timeframe_minutes
        }
      }
    }
  }
`;

// FMI stations that have at least one data source
export const GET_FMI_STATIONS_WITH_DATA = gql`
  query GetFMIStationsWithData {
    station(where: { origin: { _eq: "fmi" }, datasources: {} }) {
      id
    }
  }
`;

export const GET_DATASOURCE = gql`
  subscription GetDatasource($type: String!, $sourceId: Int!) {
    source: get_datasource(args: { type: $type, id: $sourceId }) {
      source_uuid
      source_id
      unit {
        id
        name
        symbol
      }
      name
      system_name
      type
      order
      visible
      widget_size
      datasource_annotation {
        id
        text
        color
      }
      alerts {
        id
        source_uuid
        lower_limit
        upper_limit
        channel_id
        formula_id
        active
        level
        alert_frequency_minutes
        alert_timeframe_minutes
      }
    }
  }
`;

export const GET_DATASOURCES_BY_STATION = gql`
  query GetDatasourcesByStation($stationId: Int!) {
    datasource(where: { station_id: { _eq: $stationId } }) {
      source_id
      type
      name
      source_uuid
      unit {
        symbol
      }
    }
  }
`;

export const GET_DATASOURCES_BY_UUID = gql`
  subscription GetDatasourcesByUUID($uuids: _uuid!) {
    source: get_datasources_by_uuid(args: { uuids: $uuids }) {
      source_uuid
      source_id
      unit {
        id
        name
        symbol
      }
      station {
        id
        name
        identifier
      }
      name
      system_name
      type
      order
      visible
      widget_size
      datasource_annotation {
        id
        text
        color
        timestamp
        value
      }
      alerts {
        id
        source_uuid
        lower_limit
        upper_limit
        channel_id
        formula_id
        active
        level
        alert_frequency_minutes
        alert_timeframe_minutes
      }
    }
  }
`;

export const GET_DATASOURCES_META_BY_UUID = gql`
  query GetDatasourcesMetaByUUID($uuids: _uuid!) {
    source: get_datasources_by_uuid(args: { uuids: $uuids }) {
      source_uuid
      source_id
      type
      station {
        id
        name
        identifier
      }
    }
  }
`;

export const GET_DATASOURCE_NAMES = gql`
  query GetDatasourceNames($sourceUUID: uuid) {
    datasource(where: { source_uuid: { _eq: $sourceUUID } }) {
      source_id
      name
      system_name
      type
      unit {
        symbol
      }
      station {
        name
      }
    }
  }
`;

export const GET_DATASOURCE_DATA = gql`
  query GetDatasourceData(
    $type: String!
    $sourceId: Int!
    $startDate: timestamptz!
    $endDate: timestamptz!
  ) {
    source: get_datasource(args: { type: $type, id: $sourceId }) {
      source_id
      type
      data(
        args: { start: $startDate, end: $endDate }
        order_by: { timestamp: asc }
      ) {
        timestamp
        value
        invalid
      }
    }
  }
`;

export const GET_DATASOURCE_DATA_BY_UUID = gql`
  query GetDatasourceDataByUUID(
    $sourceUUID: uuid!
    $startDate: timestamptz!
    $endDate: timestamptz!
  ) {
    source: get_datasource_by_uuid(args: { source_uuid: $sourceUUID }) {
      source_id
      source_uuid
      type
      unit {
        id
        name
        symbol
      }
      station {
        id
        name
        identifier
      }
      name
      system_name
      visible
      order
      widget_size
      datasource_annotation {
        id
        timestamp
        value
        text
        color
      }
      data(
        args: { start: $startDate, end: $endDate }
        order_by: { timestamp: asc }
      ) {
        timestamp
        value
        invalid
      }
    }
  }
`;

export const GET_MANUAL_POINT_SET = gql`
  query GetManualPointSetQuery($sourceUUID: uuid) {
    manual_point_set(where: { source_uuid: { _eq: $sourceUUID } }) {
      datapoints(order_by: { timestamp: desc }) {
        id
        timestamp
        value
        raw_value
      }
    }
  }
`;

export const GET_MANUAL_POINT_SETS = gql`
  query GetManualPointSetsQuery(
    $identifier: String
    $limit: Int!
    $offset: Int!
  ) {
    manual_point_set(
      where: { station: { identifier: { _eq: $identifier } } }
      order_by: [{ order: asc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      id
      identifier
      name
      system_name
      source_uuid
      unit {
        symbol
        id
        name
      }
      station {
        identifier
      }
      visible
    }
    manual_point_set_aggregate(
      where: { station: { identifier: { _eq: $identifier } } }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_MANUAL_POINT_SETS_COMBINED = gql`
  query GetManualPointSetsCombined(
    $searchQuery: String
    $limit: Int!
    $offset: Int!
  ) {
    manual_point_set(
      where: {
        _or: [
          { station: { name: { _ilike: $searchQuery } } }
          { station: { identifier: { _ilike: $searchQuery } } }
          { station: { organization: { name: { _ilike: $searchQuery } } } }
          { identifier: { _ilike: $searchQuery } }
          { name: { _ilike: $searchQuery } }
        ]
      }
      order_by: [{ order: asc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      id
      identifier
      name
      system_name
      source_uuid
      unit {
        symbol
        id
        name
      }
      station {
        identifier
        name
        organization {
          name
        }
      }
      visible
    }
    manual_point_set_aggregate(
      where: {
        _or: [
          { station: { name: { _ilike: $searchQuery } } }
          { station: { identifier: { _ilike: $searchQuery } } }
          { station: { organization: { name: { _ilike: $searchQuery } } } }
          { identifier: { _ilike: $searchQuery } }
          { name: { _ilike: $searchQuery } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_MANUAL_POINT_SET_BY_ID = gql`
  query GetManualPointSetById($id: Int!) {
    manual_point_set(where: { id: { _eq: $id } }) {
      id
      identifier
      name
      system_name
      source_uuid
      unit {
        symbol
        id
        name
      }
      station {
        identifier
        name
        organization {
          name
        }
      }
      visible
      datapoints(order_by: { timestamp: desc }) {
        id
        timestamp
        value
        raw_value
      }
    }
  }
`;

export const GET_DASHBOARD_STATIONS = gql`
  query GetDashboardStations($slug: String!) {
    organization(where: { slug: { _eq: $slug } }) {
      id
      name
    }
    station(where: { organization: { slug: { _eq: $slug } } }) {
      id
      identifier
      name
      primary_datasource {
        name
        type
        source_id
        source_uuid
        unit {
          symbol
        }
      }
    }
  }
`;

export const GET_ORG_REPORTING_DASHBOARDS = gql`
  query GetOrgReportingDashboards {
    reporting_dashboard {
      id
      name
      organization_id
      organization {
        name
      }
    }
  }
`;

export const GET_ORGANIZATION_DASHBOARDS = gql`
  query GetOrganizationDashboards {
    dashboard {
      id
      name
      organization_id
      organization {
        name
      }
    }
  }
`;

export const GET_DASHBOARD_STRUCTURE = gql`
  query GetDashboardStructure($id: Int!) {
    dashboard(where: { id: { _eq: $id } }) {
      id
      name
      structure
      organization_id
    }
  }
`;

export const GET_REPORTING_DASHBOARDS = gql`
  query GetReportingDashboards {
    reporting_dashboard(order_by: { name: asc }) {
      id
      name
      organization_id
      organization {
        id
        name
      }
      created_at
      updated_at
    }
  }
`;

export const GET_REPORTING_DASHBOARD_STRUCTURE = gql`
  query GetReportingDashboardStructure($id: Int!) {
    reporting_dashboard(where: { id: { _eq: $id } }) {
      id
      name
      structure
      organization_id
      created_at
      updated_at
    }
  }
`;

export const GET_VERTICAL_TEMPERATURE = gql`
  query GetVerticalTemperature(
    $stationId: Int!
    $minTimestamp: timestamptz!
    $maxTimestamp: timestamptz
  ) {
    get_vertical_temperature(
      args: {
        _max_timestamp: $maxTimestamp
        _min_timestamp: $minTimestamp
        _station_id: $stationId
      }
    ) {
      channel_id
      value
      timestamp
      system_name
    }
  }
`;

export const GET_ACCRUED_FORMULA_VALUES = gql`
  query GetAccruedFormulaValues(
    $uuids: _uuid!
    $startDate: timestamptz!
    $endDate: timestamptz!
    $timeZone: String!
  ) {
    hasura_accrued_values(
      args: {
        start: $startDate
        end: $endDate
        source_uuids: $uuids
        time_zone: $timeZone
      }
    ) {
      accrual_date
      accrued_total
    }
  }
`;

export const GET_ACCRUED_HOURLY_VALUES = gql`
  query GetAccruedHourlyValues(
    $uuids: _uuid!
    $startDate: timestamptz!
    $endDate: timestamptz!
    $timeZone: String!
  ) {
    hasura_accrued_hourly_values(
      args: {
        start: $startDate
        end: $endDate
        source_uuids: $uuids
        time_zone: $timeZone
      }
    ) {
      accrual_hour
      accrued_total
    }
  }
`;

export const GET_DISCHARGE_TABLE_SOURCES = gql`
  query GetDischargeTableSources($uuids: _uuid!) {
    source: get_datasources_by_uuid(args: { uuids: $uuids }) {
      source_uuid
      source_id
      name
      type
      unit {
        symbol
      }
      station {
        name
        identifier
      }
    }
  }
`;

export const GET_MAP_LAYERS = gql`
  query GetMapLayersQuery {
    map_layer(order_by: { display_name: asc }) {
      attribution
      display_name
      id
      max_zoom
      min_zoom
      system_name
      url
    }
  }
`;

/**
 * Shared methods
 */

export const getSourceUnitSymbol = (source: {
  type: string | null;
  unit?: { symbol: string } | null;
}) => {
  /**
   * Quick front-end hack to transform antiderivative formulas in litres to m³
   */
  if (
    source?.type === "antiderivative_formula" &&
    source?.unit?.symbol === "l"
  ) {
    return "[m³]";
  }
  return source?.unit?.symbol ? `[${source?.unit?.symbol}]` : "";
};

// Determines the front-page station list ordering: online stations first,
// then manual points, then on-hold stations, with offline stations last.
// Stations with a missing status are treated as "active" to match the
// fallback used by the status column's display logic.
const STATUS_SORT_ORDER: Record<string, number> = {
  active: 0,
  samplepoint: 1,
  hold: 2,
  inactive: 3,
};

// FMI data is only imported for weather-enabled networks; other FMI stations
// (buoys, rain stations etc.) exist but never have any data sources. An FMI
// station whose networks weren't recognised has no network rows at all.
export const isFMIDataAvailable = (station: {
  origin?: string | null;
  fmi_station_networks: { fmi_network: { weather_enabled: boolean } }[];
}): boolean =>
  (station.origin !== STATION_ORIGIN_FMI &&
    !station.fmi_station_networks?.length) ||
  !!station.fmi_station_networks?.some((n) => n.fmi_network.weather_enabled);

export const sortStationList = (
  stationList: GetMeasurementStations_station[],
  filter: (row: GetMeasurementStations_station) => boolean = () => true,
): GetMeasurementStations_station[] => {
  const sortedList = [...stationList.filter(filter)];
  sortedList.sort((a, b) => {
    const aPriority =
      STATUS_SORT_ORDER[a.status ?? "active"] ?? STATUS_SORT_ORDER.active;
    const bPriority =
      STATUS_SORT_ORDER[b.status ?? "active"] ?? STATUS_SORT_ORDER.active;

    const x = a.organization?.name.toLowerCase() || "";
    const y = b.organization?.name.toLowerCase() || "";

    const x2 = a.name.toLowerCase();
    const y2 = b.name.toLowerCase();

    return (
      aPriority - bPriority ||
      x.localeCompare(y, "fi") ||
      x2.localeCompare(y2, "fi")
    );
  });
  return sortedList;
};

// Station choices for the "show additional charts" modal: the current station
// first, then every other station that can have data, sorted. When the IDs of
// FMI stations with data are known, FMI stations without data are dropped too.
export const getAdditionalChartStationOptions = (
  stationList: GetMeasurementStations_station[],
  currentStationId: number,
  fmiStationIdsWithData?: Set<number>,
): GetMeasurementStations_station[] => {
  const currentStation = stationList.find((s) => s.id === currentStationId);
  // Filter before sorting so the sort skips the stations we drop
  const otherStations = sortStationList(
    stationList,
    (s) =>
      s.id !== currentStationId &&
      isFMIDataAvailable(s) &&
      (s.origin !== STATION_ORIGIN_FMI ||
        !fmiStationIdsWithData ||
        fmiStationIdsWithData.has(s.id)),
  );
  return currentStation ? [currentStation, ...otherStations] : otherStations;
};
